// Bát Trạch — cung phi (quái mệnh) + du niên 8 hướng, và phương vị 24 sơn.
//
// Bảng du niên KHÔNG gõ tay mà sinh bằng phép BIẾN HÀO gốc: từ quái mệnh lần
// lượt biến hào thượng → trung → hạ → trung → thượng → trung → hạ → trung, ứng
// với Sinh Khí → Ngũ Quỷ → Diên Niên → Lục Sát → Họa Hại → Thiên Y → Tuyệt Mệnh
// → Phục Vị. Cách này tự nhất quán (quan hệ đối xứng, đủ 8 quái, 4 cát cùng
// nhóm Đông/Tây tứ trạch) nên không sợ sai sót chép bảng.

export type Huong =
  | "Bắc"
  | "Đông Bắc"
  | "Đông"
  | "Đông Nam"
  | "Nam"
  | "Tây Nam"
  | "Tây"
  | "Tây Bắc";

export type Quai = "Càn" | "Đoài" | "Ly" | "Chấn" | "Tốn" | "Khảm" | "Cấn" | "Khôn";

export type DuNien =
  | "Sinh Khí"
  | "Thiên Y"
  | "Diên Niên"
  | "Phục Vị"
  | "Họa Hại"
  | "Lục Sát"
  | "Ngũ Quỷ"
  | "Tuyệt Mệnh";

export type GioiTinh = "nam" | "nu";

// ─── Phương vị ───────────────────────────────────────────────────────────────

/** 8 hướng, mỗi hướng 45°, tính từ Bắc = 0° thuận chiều kim đồng hồ. */
export const HUONG_8: Array<{ ten: Huong; goc: number; quai: Quai }> = [
  { ten: "Bắc", goc: 0, quai: "Khảm" },
  { ten: "Đông Bắc", goc: 45, quai: "Cấn" },
  { ten: "Đông", goc: 90, quai: "Chấn" },
  { ten: "Đông Nam", goc: 135, quai: "Tốn" },
  { ten: "Nam", goc: 180, quai: "Ly" },
  { ten: "Tây Nam", goc: 225, quai: "Khôn" },
  { ten: "Tây", goc: 270, quai: "Đoài" },
  { ten: "Tây Bắc", goc: 315, quai: "Càn" },
];

/** 24 sơn, mỗi sơn 15°, Tý ở chính Bắc (0°). Xếp 6 sơn/dòng cho dễ dò. */
// prettier-ignore
export const SON_24 = [
  "Tý", "Quý", "Sửu", "Cấn", "Dần", "Giáp",
  "Mão", "Ất", "Thìn", "Tốn", "Tỵ", "Bính",
  "Ngọ", "Đinh", "Mùi", "Khôn", "Thân", "Canh",
  "Dậu", "Tân", "Tuất", "Càn", "Hợi", "Nhâm",
] as const;

/** Đưa góc bất kỳ về [0, 360). */
export function chuanHoaGoc(goc: number): number {
  return ((goc % 360) + 360) % 360;
}

export function huongTuGoc(goc: number): Huong {
  const i = Math.floor((chuanHoaGoc(goc) + 22.5) / 45) % 8;
  return HUONG_8[i].ten;
}

export function sonTuGoc(goc: number): string {
  const i = Math.floor((chuanHoaGoc(goc) + 7.5) / 15) % 24;
  return SON_24[i];
}

// ─── Quái & biến hào ─────────────────────────────────────────────────────────

/** Ba hào theo thứ tự hạ → trung → thượng; 1 = dương, 0 = âm. */
const HAO: Record<Quai, [number, number, number]> = {
  Càn: [1, 1, 1],
  Đoài: [1, 1, 0],
  Ly: [1, 0, 1],
  Chấn: [1, 0, 0],
  Tốn: [0, 1, 1],
  Khảm: [0, 1, 0],
  Cấn: [0, 0, 1],
  Khôn: [0, 0, 0],
};

const QUAI_THEO_HAO = new Map<string, Quai>(
  (Object.keys(HAO) as Quai[]).map((q) => [HAO[q].join(""), q]),
);

/** Thứ tự biến hào sinh ra 8 du niên. Chỉ số hào: 0 = hạ, 1 = trung, 2 = thượng. */
const CHUOI_BIEN_HAO: Array<[number, DuNien]> = [
  [2, "Sinh Khí"],
  [1, "Ngũ Quỷ"],
  [0, "Diên Niên"],
  [1, "Lục Sát"],
  [2, "Họa Hại"],
  [1, "Thiên Y"],
  [0, "Tuyệt Mệnh"],
  [1, "Phục Vị"],
];

function dungBangDuNien(): Record<Quai, Record<Quai, DuNien>> {
  const bang = {} as Record<Quai, Record<Quai, DuNien>>;
  for (const menh of Object.keys(HAO) as Quai[]) {
    const hao = [...HAO[menh]];
    const hang = {} as Record<Quai, DuNien>;
    for (const [i, ten] of CHUOI_BIEN_HAO) {
      hao[i] = hao[i] === 1 ? 0 : 1;
      hang[QUAI_THEO_HAO.get(hao.join(""))!] = ten;
    }
    bang[menh] = hang;
  }
  return bang;
}

/** `DU_NIEN[quái mệnh][quái của hướng]` → tên du niên. */
export const DU_NIEN = dungBangDuNien();

// ─── Cung phi ────────────────────────────────────────────────────────────────

const SO_QUAI: Record<number, Quai> = {
  1: "Khảm",
  2: "Khôn",
  3: "Chấn",
  4: "Tốn",
  6: "Càn",
  7: "Đoài",
  8: "Cấn",
  9: "Ly",
};

/** Cộng dồn các chữ số về một chữ số (1–9). */
function congDon(n: number): number {
  let t = Math.abs(Math.trunc(n));
  while (t > 9) {
    t = String(t)
      .split("")
      .reduce((a, c) => a + Number(c), 0);
  }
  return t;
}

/**
 * Cung phi (quái mệnh) theo NĂM ÂM LỊCH và giới tính:
 * nam = 11 − T, nữ = T + 4, với T là tổng các chữ số của năm cộng dồn về 1 chữ
 * số. Rơi vào trung cung (5) thì nam quy về Khôn, nữ quy về Cấn.
 *
 * KHÔNG có ngoại lệ theo thế kỷ. Nhiều tài liệu ghi "từ năm 2000 đổi công thức"
 * vì họ chỉ lấy HAI chữ số cuối của năm — khi ấy mới cần bù. Ở đây dùng tổng cả
 * bốn chữ số, mà tổng chữ số luôn đồng dư với chính con số đó theo mod 9, nên
 * cung phi là hàm tuyến tính liên tục của năm. Thêm nhánh thế kỷ sẽ tạo một
 * bước nhảy sai ngay mốc 2000 (kiểm chứng: nam 2002 phải ra Đoài, không phải Càn).
 */
export function tinhCungPhi(namAmLich: number, gioiTinh: GioiTinh): Quai {
  const t = congDon(namAmLich);
  let so = gioiTinh === "nam" ? 11 - t : t + 4;
  so = ((((so - 1) % 9) + 9) % 9) + 1;
  if (so === 5) so = gioiTinh === "nam" ? 2 : 8;
  return SO_QUAI[so];
}

const DONG_TU: ReadonlySet<Quai> = new Set<Quai>(["Khảm", "Ly", "Chấn", "Tốn"]);

export function laDongTuMenh(menh: Quai): boolean {
  return DONG_TU.has(menh);
}

export function nhomMenh(menh: Quai): "Đông tứ mệnh" | "Tây tứ mệnh" {
  return laDongTuMenh(menh) ? "Đông tứ mệnh" : "Tây tứ mệnh";
}

// ─── Ý nghĩa du niên ─────────────────────────────────────────────────────────

export type MucDuNien = "đại cát" | "cát" | "tiểu cát" | "tiểu hung" | "hung" | "đại hung";

export const DU_NIEN_META: Record<
  DuNien,
  { tot: boolean; muc: MucDuNien; dam: number; tomTat: string }
> = {
  "Sinh Khí": {
    tot: true,
    muc: "đại cát",
    dam: 0.24,
    tomTat: "Tài lộc, thăng tiến, danh tiếng — hướng tốt nhất để làm việc lớn.",
  },
  "Thiên Y": {
    tot: true,
    muc: "đại cát",
    dam: 0.24,
    tomTat: "Sức khoẻ, quý nhân phù trợ — hợp kê giường ngủ, dưỡng bệnh.",
  },
  "Diên Niên": {
    tot: true,
    muc: "cát",
    dam: 0.15,
    tomTat: "Hoà hợp, bền lâu — tốt cho hôn nhân và quan hệ đối tác.",
  },
  "Phục Vị": {
    tot: true,
    muc: "tiểu cát",
    dam: 0.09,
    tomTat: "Bình an, ổn định, tập trung — hợp bàn học, thi cử, cầu con.",
  },
  "Họa Hại": {
    tot: false,
    muc: "tiểu hung",
    dam: 0.09,
    tomTat: "Thị phi, cãi vã, hao tài vặt — tránh bàn chuyện hệ trọng.",
  },
  "Lục Sát": {
    tot: false,
    muc: "hung",
    dam: 0.15,
    tomTat: "Tình duyên trắc trở, kiện tụng, tai nạn nhỏ.",
  },
  "Ngũ Quỷ": {
    tot: false,
    muc: "đại hung",
    dam: 0.24,
    tomTat: "Mất của, thị phi, bệnh tật bất ngờ — nên tránh.",
  },
  "Tuyệt Mệnh": {
    tot: false,
    muc: "đại hung",
    dam: 0.24,
    tomTat: "Tổn hại nặng về sức khoẻ và tiền bạc — hướng xấu nhất.",
  },
};

export type HuongLuan = {
  ten: Huong;
  goc: number;
  quai: Quai;
  duNien: DuNien;
  tot: boolean;
  muc: MucDuNien;
  dam: number;
  tomTat: string;
};

/** Luận đủ 8 hướng cho một cung phi, giữ nguyên thứ tự Bắc → Tây Bắc. */
export function luanTamHuong(menh: Quai): HuongLuan[] {
  return HUONG_8.map((h) => {
    const duNien = DU_NIEN[menh][h.quai];
    const meta = DU_NIEN_META[duNien];
    return { ten: h.ten, goc: h.goc, quai: h.quai, duNien, ...meta };
  });
}

// ─── Lấy năm sinh / giới tính từ hồ sơ ───────────────────────────────────────

export function doiGioiTinh(gender?: string | null): GioiTinh | null {
  const g = String(gender ?? "")
    .toLowerCase()
    .trim();
  if (g === "male" || g === "nam") return "nam";
  if (g === "female" || g === "nu" || g === "nữ") return "nu";
  return null;
}

/**
 * Năm âm lịch dùng để lập cung phi. Ưu tiên `lunar_birth_year` do backend tính;
 * nếu chỉ có ngày dương thì lấy năm dương — lưu ý người sinh tháng 1–2 trước
 * Tết sẽ lệch một năm, nên nơi gọi cần nhắc người dùng bổ sung lịch âm.
 */
export function namAmLichSinh(profile?: {
  lunar_birth_year?: number | null;
  birth_date?: string | null;
}): { nam: number; chinhXac: boolean } | null {
  if (profile?.lunar_birth_year) return { nam: profile.lunar_birth_year, chinhXac: true };
  const raw = profile?.birth_date;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  // Sinh trước 20/2 có thể vẫn thuộc năm âm lịch trước đó (Tết muộn nhất ~20/2).
  const chinhXac = !(d.getMonth() === 0 || (d.getMonth() === 1 && d.getDate() <= 20));
  return { nam: d.getFullYear(), chinhXac };
}
